// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { ClipboardService } from 'src/app/modules/shared/services/clipboard.service';
import {
  PlannedActivitiesService,
  TSortKey,
} from 'src/app/modules/shared/services/planned-activities/planned-activities.service';
import {
  ActivityStatus,
  TicketType,
} from '../../../../_interfaces/service-ticket-model';
import { APICallGateComponent } from '../../../shared/components/api-call-gate.component';
import { LocalizedDatePipe } from '../../../shared/pipes/localizedDate.pipe';
import { ScreenLayoutService } from '../../../shared/services/screen-layout-service/screen-layout.service';
import { SettingsService } from '../../../shared/services/settings.service';
import { ShuiDatepickerDirective } from '../../../shared/utils/shuiDatepicker.directive';
import { RouteService } from '../../services/routeService.service';
import { ServiceTicketsService } from '../../services/service-tickets.service';
import { BackToSystemOverviewComponent } from '../backToSystemOverview/backToSystemOverview.component';

@Component({
  selector: 'app-planned-activities-page',
  templateUrl: './planned-activities-page.component.html',
  styleUrls: [
    './planned-activities-page.component.scss',
    '../service-tickets-page/service-tickets-shared.scss',
  ],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BackToSystemOverviewComponent,
    APICallGateComponent,
    ShuiDatepickerDirective,
    LocalizedDatePipe,
    TranslateModule,
    CommonModule,
  ],
})
export class PlannedActivitiesPageComponent {
  private routeService = inject(RouteService);
  protected clipboard = inject(ClipboardService);
  protected plannedActivitiesService = inject(PlannedActivitiesService);
  protected screenLayoutService = inject(ScreenLayoutService);
  protected serviceTicketsService = inject(ServiceTicketsService);
  protected defaultFilters = this.plannedActivitiesService.getDefaultFilters();
  protected settingsService = inject(SettingsService);

  // Transient state
  filters$ = new BehaviorSubject(this.defaultFilters);
  dateBoundsStart$ = new BehaviorSubject({
    minDate: 0,
    maxDate: 0,
  });
  dateBoundsEnd$ = new BehaviorSubject({
    minDate: 0,
    maxDate: 0,
  });

  // Store the initial date bounds for reset
  private initialDateBoundsStart: { minDate: number; maxDate: number } | null =
    null;

  private initialDateBoundsEnd: { minDate: number; maxDate: number } | null =
    null;

  constructor() {
    this.initializeActivityFilters();
  }

  // Called when settings change to update date bounds and filters
  private updateDateBoundsAndFiltersOnSettingsChange(): void {
    // Use the initial bounds for reset if available, else fallback to default
    const dateBoundsStart = this.initialDateBoundsStart ?? {
      minDate: this.defaultFilters.daterangeStart[0].getTime(),
      maxDate: this.defaultFilters.daterangeStart[1].getTime(),
    };
    const dateBoundsEnd = this.initialDateBoundsEnd ?? {
      minDate: this.defaultFilters.daterangeEnd[0].getTime(),
      maxDate: this.defaultFilters.daterangeEnd[1].getTime(),
    };
    // Get the current filters
    const currentFilters = this.filters$.getValue();
    // Recalculate only the date-related fields using the service's reset logic
    const recalculatedFilters =
      this.plannedActivitiesService.performFilterReset(
        dateBoundsStart,
        dateBoundsEnd,
        this.defaultFilters,
      );
    // Merge recalculated date fields into the current filters, preserving user-applied filters
    const mergedFilters = {
      ...currentFilters,
      daterangeStart: recalculatedFilters.daterangeStart,
      daterangeEnd: recalculatedFilters.daterangeEnd,
    };
    this.filters$.next(mergedFilters);
    this.dateBoundsStart$.next({
      minDate: mergedFilters.daterangeStart[0].getTime(),
      maxDate: mergedFilters.daterangeStart[1].getTime(),
    });
    this.dateBoundsEnd$.next({
      minDate: mergedFilters.daterangeEnd[0].getTime(),
      maxDate: mergedFilters.daterangeEnd[1].getTime(),
    });
  }

  settingsChangeSubscription$ = this.settingsService.settings$
    .pipe(takeUntilDestroyed())
    .subscribe(() => {
      // Recalculate date bounds and filters when settings change (date format or timeline order)
      // This ensures the date fields are updated in the UI
      this.updateDateBoundsAndFiltersOnSettingsChange();
    });

  // Date observables for validation and UI formatting
  dateBoundsValid$ = {
    start: this.plannedActivitiesService.createDateValidationObservable(
      this.dateBoundsStart$,
    ),
    end: this.plannedActivitiesService.createDateValidationObservable(
      this.dateBoundsEnd$,
    ),
  };

  // UI-formatted date bounds
  dateBoundsForShui$ = {
    start: this.plannedActivitiesService.createFormattingObservable(
      this.dateBoundsStart$,
    ),
    end: this.plannedActivitiesService.createFormattingObservable(
      this.dateBoundsEnd$,
    ),
  };

  // Main data streams
  tickets$ = this.serviceTicketsService.getTickets.lastSuccessfulData$.pipe(
    map((tickets) =>
      this.plannedActivitiesService.processTickets(
        tickets,
        (bounds) => this.dateBoundsStart$.next(bounds),
        (bounds) => this.dateBoundsEnd$.next(bounds),
        (filters) => this.updateFilters(filters),
      ),
    ),
  );

  ticketDateBoundsSubscription$ = this.tickets$
    .pipe(takeUntilDestroyed())
    .subscribe(() => {
      if (
        !this.initialDateBoundsStart &&
        this.dateBoundsStart$.getValue().minDate &&
        this.dateBoundsStart$.getValue().maxDate
      ) {
        this.initialDateBoundsStart = { ...this.dateBoundsStart$.getValue() };
      }
      if (
        !this.initialDateBoundsEnd &&
        this.dateBoundsEnd$.getValue().minDate &&
        this.dateBoundsEnd$.getValue().maxDate
      ) {
        this.initialDateBoundsEnd = { ...this.dateBoundsEnd$.getValue() };
      }
    });

  // Count of all tickets
  totalTicketsCount$ = this.tickets$.pipe(map((tickets) => tickets.length));

  // All available activity states from data
  activityStates$ = this.tickets$.pipe(
    map((tickets) =>
      this.plannedActivitiesService.createUniqueValuesList<
        ActivityStatus,
        'activityStatus'
      >(tickets, 'activityStatus', ActivityStatus.All),
    ),
  );

  // All available ticket types from data
  ticketTypes$ = this.tickets$.pipe(
    map((tickets) =>
      this.plannedActivitiesService.createUniqueValuesList<
        TicketType,
        'ticketType'
      >(tickets, 'ticketType', TicketType.All),
    ),
  );

  // Filtered and sorted tickets based on current filters
  processedTickets$ = combineLatest({
    tickets: this.tickets$,
    dateBoundsStart: this.dateBoundsStart$,
    dateBoundsEnd: this.dateBoundsEnd$,
    filters: this.filters$,
  }).pipe(
    map(({ tickets, filters }) =>
      this.plannedActivitiesService.processTicketsWithFilters(tickets, filters),
    ),
  );

  // Count of filtered tickets
  processedTicketsCount$ = this.processedTickets$.pipe(
    map((tickets) => tickets.length),
  );

  // Date range for scheduled activities - from today to the max date
  scheduledDateBoundsEndDates$ = this.dateBoundsEnd$.pipe(
    map(({ maxDate }) => [new Date(), new Date(maxDate)]),
  );

  // Date range for scheduling start dates
  scheduledDateBoundsStartDates$ = this.dateBoundsStart$.pipe(
    map(({ maxDate }) => [new Date(), new Date(maxDate)]),
  );

  // Date range for overdue activities - from min date to today
  overdueDateBoundsEndDates$ = this.dateBoundsEnd$.pipe(
    map(({ minDate }) => {
      const today = new Date();
      return [new Date(minDate), today];
    }),
  );

  // Date range for overdue start dates
  overdueDateBoundsStartDates$ = this.dateBoundsStart$.pipe(
    map(({ minDate }) => {
      const today = new Date();
      return [new Date(minDate), today];
    }),
  );

  // Initialize filters and set up data fetching
  initializeActivityFilters(): void {
    this.setupSystemBasedTicketFetching();
    this.setupQueryParamsHandling();
  }

  // Update filters using the service method
  updateFilters(filters: Partial<typeof this.defaultFilters>): void {
    const updatedFilters = this.plannedActivitiesService.updateFilters(
      this.filters$.getValue(),
      filters,
    );
    this.filters$.next(updatedFilters);
  }

  // Reset date range to bounds
  clearAndResetDateRange(): void {
    this.updateFilters(
      this.plannedActivitiesService.clearAndResetDateRange(
        this.dateBoundsStart$.getValue(),
        this.dateBoundsEnd$.getValue(),
        this.filters$.getValue(),
      ),
    );
  }

  // UI-event handlers - all update the filters with different parameters
  onDateRangeStartChanged(daterangeStart: [Date, Date]): void {
    this.handleDateRangeChange('daterangeStart', daterangeStart);
  }

  onDateRangeEndChanged(daterangeEnd: [Date, Date]): void {
    this.handleDateRangeChange('daterangeEnd', daterangeEnd);
  }

  onChangeSort(sortKey: TSortKey, sortDescending: boolean): void {
    this.updateFilters({ sortDescending, sortKey });
  }

  onActivityStatusChanged(activityStatus: ActivityStatus): void {
    this.updateFilters({ activityStatus });
  }

  onActivityTypeChanged(activityType: TicketType): void {
    this.updateFilters({ ticketType: activityType });
  }

  onSearchString(searchString: string): void {
    this.updateFilters({ searchString });
  }

  // Helper to reset all filters
  resetFilters(): void {
    // Use the initial bounds for reset if available
    const dateBoundsStart = this.initialDateBoundsStart ?? {
      minDate: this.defaultFilters.daterangeStart[0].getTime(),
      maxDate: this.defaultFilters.daterangeStart[1].getTime(),
    };
    const dateBoundsEnd = this.initialDateBoundsEnd ?? {
      minDate: this.defaultFilters.daterangeEnd[0].getTime(),
      maxDate: this.defaultFilters.daterangeEnd[1].getTime(),
    };
    const defaultFilters = this.plannedActivitiesService.performFilterReset(
      dateBoundsStart,
      dateBoundsEnd,
      this.defaultFilters,
    );
    this.filters$.next(defaultFilters);
    this.dateBoundsStart$.next({
      minDate: defaultFilters.daterangeStart[0].getTime(),
      maxDate: defaultFilters.daterangeStart[1].getTime(),
    });
    this.dateBoundsEnd$.next({
      minDate: defaultFilters.daterangeEnd[0].getTime(),
      maxDate: defaultFilters.daterangeEnd[1].getTime(),
    });
  }

  // UI helper for text overflow detection
  isOverflowing(el: HTMLElement): boolean {
    if (!el.style.overflow || el.style.overflow === 'visible') {
      el.style.overflow = 'hidden';
    }
    return el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
  }

  private handleDateRangeChange<K extends 'daterangeStart' | 'daterangeEnd'>(
    filterProperty: K,
    dateRange: [Date, Date],
  ): void {
    const startIso = dateRange[0].toISOString();
    const endIso = dateRange[1].toISOString();

    // Force the dates to be exactly as selected by the user
    this.updateFilters({
      [filterProperty]: dateRange,
      // Set a flag to bypass reset checks
      resetFromOverdue: false,
      resetFromScheduled: false,
      // Include the ISO strings in a field that will be ignored by filter processor
      // This prevents SonarQube warnings about unused return values
      _iso: [startIso, endIso],
    } as Partial<typeof this.defaultFilters>);
  }

  // Fetch tickets based on the system from route
  private setupSystemBasedTicketFetching(): void {
    this.routeService.currentSystem$
      .pipe(takeUntilDestroyed())
      .subscribe((system) => {
        if (!system) return;

        this.serviceTicketsService.getTickets.request(
          {
            systemMaterialNumber: system.systemMaterialNumber,
            systemSerialNumber: system.systemSerialNumber,
          },
          { clearStaleData: false },
        );
      });
  }

  // Handle query parameters for activity filtering
  private setupQueryParamsHandling(): void {
    this.routeService.queryParams$
      .pipe(takeUntilDestroyed())
      .subscribe((params) => {
        if (params) {
          this.plannedActivitiesService.processQueryParams(
            params as Record<string, string>,
            () => this.resetFilters(),
            (filters) => this.updateFilters(filters),
          );
        }
      });
  }
}
